import React, { useState, useEffect, SetStateAction, Dispatch } from 'react';
import { Box, Dialog, DialogActions, DialogContent, DialogTitle, Table, TableBody, TableCell, TableHead, TableRow, Button, TextField, ButtonGroup, CircularProgress, Snackbar, Alert } from '@mui/material';
import axios from 'axios';
import { MachineInfo } from './types';
import { API_PATH } from '../../../App';
import { getAxiosConfig } from '../../../helpers/AuthHelper';
import SingleSelectChip2 from '../GlobalComponents/SingleSelectChip2';
import MachineOnOffButtonGroup from './MachineOnOffButtonGroup';
import { isHashVendorAllowed, restrictTargetPool } from './machineConfigRules';

interface ConfigModalProps {
    open: boolean;
    onClose: () => void;
    data: MachineInfo[];
    trigger: boolean;
    setTrigger: Dispatch<SetStateAction<boolean>>;

}

export interface MachineConfigRowState {
    id: number;
    modelName: string;
    hashVendor: number | null;
    targetPool: number | null;
    workerId: string | null;
    organization: number | null;
    resellerId: number | null;
    onOff: 'ON' | 'OFF' | 'NEW';
    data: MachineInfo;
}

const ConfigModal: React.FC<ConfigModalProps> = ({ open, onClose, data = [], trigger, setTrigger }) => {
    const [rowsState, setRowsState] = useState<MachineConfigRowState[]>([]);
    const [allHashVendor, setAllHashVendor] = useState<number | null>(null);
    const [allTargetPool, setAllTargetPool] = useState<number | null>(null);
    const [manualWorkerId, setManualWorkerId] = useState<string | null>(null);
    const [allOnOff, setAllOnOff] = useState<'ON' | 'OFF' | 'NEW' | null>(null);

    const [hashVendorList, setHashVendorList] = useState<{ name: string }[]>([]);
    const [targetPoolList, setTargetPoolList] = useState<string[]>([]);
    const [tenantList, setTenantList] = useState<{ id: number, tenant: string }[]>([]);
    const [resellerList, setResellerList] = useState<{ id: number, resellerName: string }[]>([]);
    const [allOrganization, setAllOrganization] = useState<number | null>(null);
    const [allReseller, setAllReseller] = useState<number | null>(null);

    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [alertMessage, setAlertMessage] = useState<string>('');

    useEffect(() => {
        if (data.length > 0) {
            setRowsState(data.map(item => {
                const targetPool = restrictTargetPool(item.hostingBillingScheme, item.targetPool);
                const hashVendorIndex = item.hashVendor
                    ? hashVendorList.findIndex(vendor => vendor.name === item.hashVendor)
                    : allHashVendor;
                const hashVendor = isHashVendorAllowed(item.hostingBillingScheme, item.hashVendor)
                    ? hashVendorIndex
                    : null;

                return {
                    id: item.id,
                    modelName: item.modelName,
                    hashVendor,
                    targetPool: targetPool ? targetPoolList.indexOf(targetPool) : allTargetPool ?? null,
                    workerId: item.workerId || null,
                    organization: item.organization ? tenantList.findIndex(t => t.tenant === item.organization) : allOrganization,
                    resellerId: item.resellerId ? resellerList.findIndex(r => r.id === item.resellerId) : allReseller,
                    onOff: item.machineStatus as 'ON' | 'OFF' | 'NEW',
                    data: item,
                };
            }));
        }
        setAlertMessage('')
    }, [data, hashVendorList, targetPoolList, tenantList, resellerList]);

    useEffect(() => {
        const fetchLists = () => {
            Promise.all([
                axios.get(API_PATH + '/machines/hash_vendor', getAxiosConfig()),
                axios.get(API_PATH + '/machines/target_pools', getAxiosConfig()),
                axios.get(API_PATH + '/machines/equalizer_tenants', getAxiosConfig()),
                axios.get(API_PATH + '/customer_ledger/allResellers', getAxiosConfig())
            ])
                .then(([hashVendorResponse, targetPoolResponse, tenantResponse, resellerResponse]) => {
                    setHashVendorList(hashVendorResponse.data);
                    setTargetPoolList(targetPoolResponse.data);
                    setTenantList(tenantResponse.data);
                    setResellerList(resellerResponse.data);
                })
                .catch(error => {
                    console.error('Error fetching data:', error);
                });
        };

        fetchLists();
    }, []);

    const autofillWorkerIds = (firstId: string) => {
        const match = firstId.match(/^(.*?)(\d*)$/);
        if (!match || match[2] === '') {
            const clearedWorkerIds = rowsState.map(row => ({ ...row, workerId: firstId || '' }));
            setRowsState(clearedWorkerIds);
            return;
        }
        const prefix = match[1];
        let startNumber = parseInt(match[2], 10);
        const numberLength = match[2].length;
        if (isNaN(startNumber)) {
            const resetWorkerIds = rowsState.map(row => ({ ...row, workerId: firstId }));
            setRowsState(resetWorkerIds);
            return;
        }
        const newWorkerIds = rowsState.map((row, index) => {
            const newWorkerId = `${prefix}${String(startNumber + index).padStart(numberLength, '0')}`;
            return { ...row, workerId: newWorkerId };
        });

        setRowsState(newWorkerIds);
    };

    // Returns the targetPool index for a given hashVendor index, based on vendor name.
    // CashLab, Segment → SEG_POOL; NiceHash, Equalizer → F2POOL
    const getTargetPoolForVendor = (
        vendorIndex: number | null,
        hostingBillingScheme?: string
    ): number | null => {
        const vendorName = vendorIndex === null
            ? null
            : hashVendorList[vendorIndex]?.name?.toUpperCase();
        let targetPool: string | null = null;
        if (vendorName === 'CASHLAB' || vendorName === 'SEGMENT') {
            targetPool = 'SEG_POOL';
        } else if (vendorName === 'NICEHASH' || vendorName === 'EQUALIZER') {
            targetPool = 'F2POOL';
        }

        const restrictedTargetPool = restrictTargetPool(hostingBillingScheme, targetPool);
        const idx = targetPoolList.findIndex(pool => pool === restrictedTargetPool);
        return idx >= 0 ? idx : null;
    };

    const handleAllChange = (field: 'hashVendor' | 'targetPool' | 'organization' | 'resellerId', value: number | null) => {
        setRowsState(prevState => {
            return prevState.map(row => {
                const updatedRow = { ...row, [field]: value };
                if (field === 'hashVendor') {
                    updatedRow.targetPool = getTargetPoolForVendor(value, row.data.hostingBillingScheme);
                }
                return updatedRow;
            });
        });

        if (field === 'hashVendor') {
            setAllHashVendor(value);
            setAllTargetPool(getTargetPoolForVendor(value));
        } else if (field === 'targetPool') {
            setAllTargetPool(value);
        } else if (field === 'organization') {
            setAllOrganization(value);
        } else if (field === 'resellerId') {
            setAllReseller(value);
        }
    };

    const handleRowChange = (index: number, field: keyof MachineConfigRowState, value: number | string | 'ON' | 'OFF' | 'NEW' | null) => {
        let updatedRow = { ...rowsState[index], [field]: value };

        if (field === 'hashVendor') {
            updatedRow.targetPool = getTargetPoolForVendor(
                value as number | null,
                updatedRow.data.hostingBillingScheme
            );
        }

        setRowsState(prevState =>
            prevState.map((row, i) => (i === index ? updatedRow : row))
        );
    };

    const handleButtonGroupChange = (index: number, value: 'ON' | 'OFF' | 'NEW') => {
        handleRowChange(index, 'onOff', value);
    };

    const toggleAllSwitches = (value: 'ON' | 'OFF') => {
        setAllOnOff(value);
        setRowsState(prevState =>
            prevState.map(row => ({
                ...row,
                onOff: value
            }))
        );
    };

    const formatDataForSave = () => {
        return rowsState.map(row => ({
            id: row.id,
            workerId: row.workerId || '',
            targetPool: restrictTargetPool(
                row.data.hostingBillingScheme,
                targetPoolList[row.targetPool ?? 0]
            ) || 'NONE',
            hashVendor: hashVendorList[row.hashVendor ?? 0]?.name || 'NONE',
            state: hashVendorList[row.hashVendor ?? 0]?.name === 'NICEHASH' ? "NEW" : (row.onOff === 'NEW' ? 'NEW' : row.onOff),
            organization: (() => {
                const isNewVendorEqualizer = hashVendorList[row.hashVendor ?? 0]?.name === 'EQUALIZER';
                const isCurrentlyEqualizer = row.data.hashVendor === 'EQUALIZER';

                if (isNewVendorEqualizer) {
                    return (row.organization !== null) ? tenantList[row.organization as number]?.tenant : (row.data.organization || 'default');
                } else if (isCurrentlyEqualizer) {
                    return row.data.organization || 'default';
                }
                return 'default';
            })(),
            resellerId: row.resellerId !== null ? resellerList[row.resellerId]?.id : null
        }));
    };

    const handleSave = () => {
        setLoading(true);

        const invalidAutoSettlementRows = rowsState.filter(row =>
            !isHashVendorAllowed(
                row.data.hostingBillingScheme,
                row.hashVendor === null ? null : hashVendorList[row.hashVendor]?.name
            )
        );

        if (invalidAutoSettlementRows.length > 0) {
            setSuccess(false);
            setAlertMessage('AUTO_SETTLEMENT machines require Hash Vendor SEGMENT or CASHLAB.');
            setLoading(false);
            return;
        }

        const formattedData = formatDataForSave();

        const invalidItems = formattedData.filter(
            item => item.hashVendor === 'EQUALIZER' && (!item.organization || item.organization === 'default')
        );

        if (invalidItems.length > 0) {
            setSuccess(false);
            setAlertMessage('Organization is required when Hash Vendor is EQUALIZER.');
            setLoading(false);
            return;
        }

        axios.post(API_PATH + '/machines/update', formattedData, getAxiosConfig())
            .then(response => {
                const { data } = response;
                const allSuccess = data.every((item: { status: boolean; }) => item.status === true);

                if (allSuccess) {
                    setSuccess(true);
                    setAlertMessage('Data saved successfully!');
                    setTimeout(() => {
                        setRowsState([]);
                        setAllHashVendor(null);
                        setAllTargetPool(null);
                        setManualWorkerId(null);
                        setAllOnOff(null);
                        onClose();
                        setTrigger(!trigger)
                    }, 2000);

                } else {
                    const failureReasons = data
                        .filter((item: { status: any; }) => !item.status)
                        .map((item: { reason: any; }) => item.reason)
                        .join(', ');

                    setSuccess(false);
                    setAlertMessage(`Failed to save some data: ${failureReasons}`);
                }
            })
            .catch(error => {
                console.error('Error saving data:', error);
                setSuccess(false);
                setAlertMessage('An error occurred while saving data.');
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const handleCloseAlert = () => {
        setSuccess(false);
    };

    const handleCloseModal = () => {
        onClose()
        setAllHashVendor(null)
        setAllTargetPool(null)
        setAllOrganization(null)
    }

    return (
        <Dialog open={open} onClose={handleCloseModal} fullWidth maxWidth="lg">
            <DialogTitle>Configure Machines</DialogTitle>
            <DialogContent>
                <Table style={{ width: '100%' }}>
                    <TableHead>
                        <TableRow>
                            <TableCell style={{ width: '10%' }}><strong>ID</strong></TableCell>
                            <TableCell style={{ width: '10%' }}><strong>Model</strong></TableCell>
                            <TableCell style={{ width: '15%' }}><strong>Hash Vendor</strong></TableCell>
                            <TableCell style={{ width: '15%', textAlign: 'center' }}><strong>Target Pool</strong></TableCell>
                            <TableCell style={{ width: '15%' }}><strong>Worker ID</strong></TableCell>
                            <TableCell style={{ width: '15%' }}><strong>Affiliate</strong></TableCell>
                            <TableCell style={{ width: '10%' }}><strong>On/Off</strong></TableCell>
                        </TableRow>
                        {data.length > 1 &&
                            <TableRow>
                                <TableCell></TableCell>
                                <TableCell></TableCell>
                                <TableCell>
                                    <SingleSelectChip2
                                        dataList={hashVendorList
                                            .map((vendor, index) => ({ id: index, name: vendor.name }))
                                            .filter(vendor => data.every(item =>
                                                isHashVendorAllowed(item.hostingBillingScheme, vendor.name)
                                            ))}
                                        selectedId={allHashVendor ?? null}
                                        setSelectedId={(id) => handleAllChange('hashVendor', id)}
                                        name="Hash Vendor"
                                    />
                                </TableCell>
                                <TableCell>
                                    {/* <SingleSelectChip2
                                        dataList={targetPoolList.map((pool, index) => ({ id: index, name: pool }))}
                                        selectedId={allTargetPool ?? null}
                                        setSelectedId={(id) => handleAllChange('targetPool', id)}
                                        name="Target Pool"
                                    /> */}
                                    {hashVendorList[allHashVendor ?? -1]?.name === 'EQUALIZER' && (
                                        <SingleSelectChip2
                                            dataList={tenantList.map((t, index) => ({ id: index, name: t.tenant }))}
                                            selectedId={allOrganization ?? null}
                                            setSelectedId={(id) => handleAllChange('organization', id)}
                                            name="Organization"
                                        />
                                    )}
                                </TableCell>
                                <TableCell>
                                    <TextField
                                        label="Worker ID"
                                        size='small'
                                        value={manualWorkerId || ''}
                                        onChange={(e) => {
                                            const newId = e.target.value;
                                            setManualWorkerId(newId);
                                            autofillWorkerIds(newId);
                                        }}
                                    />
                                </TableCell>
                                <TableCell>
                                    <SingleSelectChip2
                                        dataList={resellerList.map((r, index) => ({ id: index, name: r.resellerName }))}
                                        selectedId={allReseller ?? null}
                                        setSelectedId={(id) => handleAllChange('resellerId', id)}
                                        name="Affiliate"
                                    />
                                </TableCell>
                                <TableCell>
                                    <ButtonGroup variant="outlined" aria-label="On/Off button group">
                                        <Button
                                            onClick={() => toggleAllSwitches('ON')}
                                            variant={allOnOff === 'ON' ? 'contained' : 'outlined'}
                                            disabled={rowsState.some(t => t.targetPool === 1 && hashVendorList[t.hashVendor ?? -1]?.name !== 'EQUALIZER')}
                                        >
                                            On
                                        </Button>
                                        <Button
                                            onClick={() => toggleAllSwitches('OFF')}
                                            variant={allOnOff === 'OFF' ? 'contained' : 'outlined'}
                                            disabled={rowsState.some(t => t.targetPool === 1 && hashVendorList[t.hashVendor ?? -1]?.name !== 'EQUALIZER')}
                                        >
                                            Off
                                        </Button>
                                        <Button disabled>New</Button>
                                    </ButtonGroup>
                                </TableCell>
                            </TableRow>
                        }
                    </TableHead>
                    <TableBody>
                        {rowsState.map((row, index) => (
                            <TableRow key={row.id}>
                                <TableCell style={{ width: '10%' }}>{row.id}</TableCell>
                                <TableCell style={{ width: '10%' }}>{row.modelName}</TableCell>
                                <TableCell style={{ width: '20%' }}>
                                    <SingleSelectChip2
                                        dataList={hashVendorList
                                            .map((vendor, idx) => ({ id: idx, name: vendor.name }))
                                            .filter(vendor =>
                                                isHashVendorAllowed(row.data.hostingBillingScheme, vendor.name)
                                            )}
                                        selectedId={row.hashVendor ?? null}
                                        setSelectedId={(id) => handleRowChange(index, 'hashVendor', id)}
                                        name="Hash Vendor"
                                    />
                                </TableCell>
                                <TableCell style={{ width: '20%', textAlign: 'center', color: "green", fontWeight: 'bold' }}>
                                    {/* <SingleSelectChip2
                                        dataList={targetPoolList.map((pool, idx) => ({ id: idx, name: pool }))}
                                        selectedId={row.targetPool ?? null}
                                        setSelectedId={(id) => handleRowChange(index, 'targetPool', id)}
                                        name="Target Pool"
                                    /> */}
                                    {row.targetPool !== null && row.targetPool !== undefined ? targetPoolList[row.targetPool] ?? null : null}
                                    {hashVendorList[row.hashVendor ?? -1]?.name === 'EQUALIZER' && (
                                        <SingleSelectChip2
                                            dataList={tenantList.map((t, idx) => ({ id: idx, name: t.tenant }))}
                                            selectedId={typeof row.organization === 'number' ? row.organization : null}
                                            setSelectedId={(id) => handleRowChange(index, 'organization', id)}
                                            name="Organization"
                                        />
                                    )}
                                </TableCell>
                                <TableCell style={{ width: '15%' }}>
                                    <TextField
                                        label="Worker ID"
                                        size='small'
                                        value={row.workerId || ''}
                                        onChange={(e) => handleRowChange(index, 'workerId', e.target.value)}
                                    />
                                </TableCell>
                                <TableCell style={{ width: '15%' }}>
                                    <SingleSelectChip2
                                        dataList={resellerList.map((r, idx) => ({ id: idx, name: r.resellerName }))}
                                        selectedId={typeof row.resellerId === 'number' ? row.resellerId : null}
                                        setSelectedId={(id) => handleRowChange(index, 'resellerId', id)}
                                        name="Affiliate"
                                    />
                                </TableCell>
                                <TableCell style={{ width: '10%' }}>
                                    <MachineOnOffButtonGroup row={row} index={index} handleButtonGroupChange={handleButtonGroupChange} disabled={hashVendorList[row.hashVendor ?? -1]?.name === 'NICEHASH'} />
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="primary">
                    Close
                </Button>
                <Button onClick={handleSave} color="primary" disabled={loading}>
                    {loading ? <CircularProgress size={24} /> : 'Save'}
                </Button>
            </DialogActions>
            <Snackbar open={!!alertMessage} autoHideDuration={6000} onClose={handleCloseAlert}>
                <Alert onClose={handleCloseAlert} severity={success ? "success" : "error"}>
                    {alertMessage}
                </Alert>
            </Snackbar>
        </Dialog >
    );
};

export default ConfigModal;
